import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useAuth } from "./authProvider";
import adminTicketService from "../../services/adminTicketService";

const TICKET_POLL_INTERVAL_SEC = 60;

const AdminTicketUnreadContext = createContext({
  menuBadgeCount: 0,
  ticketSnapshots: [],
  refreshSummary: async () => {},
});

export function AdminTicketUnreadProvider({ children }) {
  const { user } = useAuth() || {};
  const [menuBadgeCount, setMenuBadgeCount] = useState(0);
  const [ticketSnapshots, setTicketSnapshots] = useState([]);
  const pollingTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const refreshSummary = useCallback(async () => {
    if (!user) {
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      return;
    }
    try {
      const res = await adminTicketService.getSummary();
      if (!res.pollingEnabled) {
        setMenuBadgeCount(0);
        setTicketSnapshots([]);
        return;
      }
      setMenuBadgeCount(Number(res.menuBadgeCount) || 0);
      setTicketSnapshots(Array.isArray(res.tickets) ? res.tickets : []);
    } catch (e) {
      console.warn("admin ticket summary poll failed", e);
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
    }
  }, [user]);

  useEffect(() => {
    stopPolling();
    if (!user) {
      setMenuBadgeCount(0);
      setTicketSnapshots([]);
      return undefined;
    }
    refreshSummary();
    const ms = Math.max(1, TICKET_POLL_INTERVAL_SEC) * 1000;
    pollingTimerRef.current = setInterval(refreshSummary, ms);
    return stopPolling;
  }, [user, refreshSummary, stopPolling]);

  const value = useMemo(
    () => ({ menuBadgeCount, ticketSnapshots, refreshSummary }),
    [menuBadgeCount, ticketSnapshots, refreshSummary]
  );

  return (
    <AdminTicketUnreadContext.Provider value={value}>
      {children}
    </AdminTicketUnreadContext.Provider>
  );
}

export function useAdminTicketUnread() {
  return useContext(AdminTicketUnreadContext);
}

AdminTicketUnreadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
 